import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, LogOut, ChevronDown, LayoutDashboard, UserCircle } from 'lucide-react';
import { SiFacebook } from 'react-icons/si';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { navItems } from '@/data/index';
import { ROUTE_PATHS } from '@/lib/index';

export function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check login state from sessionStorage
  // Re-check on every route change (sessionStorage doesn't fire 'storage' event in same tab)
  useEffect(() => {
    const role = sessionStorage.getItem('hktcm_role');
    setIsLoggedIn(!!role);
    setUserRole(role);
  }, [location.pathname]);

  function handleLogout() {
    sessionStorage.removeItem('hktcm_role');
    sessionStorage.removeItem('hktcm_user');
    setIsLoggedIn(false);
    setUserRole(null);
    navigate(ROUTE_PATHS.HOME);
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) resizeObserver.observe(headerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Smooth scroll to anchor section
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background/95 backdrop-blur-md shadow-md'
            : 'bg-background/80 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="#hero"
              onClick={(e) => handleAnchorClick(e, '#hero')}
              className="flex items-center gap-3"
            >
              <img
                src="/images/hkcmprgu-logo-01.png"
                alt="香港中醫師權益總工會"
                className="h-12 w-auto"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <span className="text-xl font-bold text-primary hidden sm:inline">
                香港中醫師權益總工會
              </span>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) =>
                item.label === '關於我們' ? (
                  /* About Us with click-toggle dropdown */
                  <div
                    key={item.href}
                    className="relative"
                  >
                    <button
                      onClick={() => setAboutOpen(prev => !prev)}
                      className="flex items-center gap-1 text-foreground hover:text-primary transition-colors font-medium relative group bg-transparent border-0 cursor-pointer"
                    >
                      {item.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`} />
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                    </button>
                    {aboutOpen && (
                      <>
                        {/* Backdrop to close on outside click */}
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setAboutOpen(false)}
                        />
                        <div className="absolute top-full left-0 mt-2 w-36 bg-background/95 backdrop-blur-md border border-border rounded-xl shadow-lg py-1 z-50">
                          {/* Scroll to #about anchor */}
                          <a
                            href="#about"
                            onClick={(e) => { handleAnchorClick(e, '#about'); setAboutOpen(false); }}
                            className="block px-4 py-2.5 text-sm text-foreground hover:text-primary hover:bg-primary/5 transition-colors rounded-lg mx-1"
                          >
                            關於我們
                          </a>
                          <Link
                            to={ROUTE_PATHS.ORG_CHART}
                            onClick={() => setAboutOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground hover:text-primary hover:bg-primary/5 transition-colors rounded-lg mx-1"
                          >
                            組織架構
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleAnchorClick(e, item.href)}
                    className="text-foreground hover:text-primary transition-colors font-medium relative group"
                  >
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                  </a>
                )
              )}
              {isLoggedIn ? (
                <div className="flex items-center gap-2">
                  {userRole === 'admin' ? (
                    <Link to={ROUTE_PATHS.MEMBER_DASHBOARD}>
                      <Button variant="outline" size="sm" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                        <LayoutDashboard className="h-4 w-4" />
                        管理員後台
                      </Button>
                    </Link>
                  ) : (
                    <Link to={ROUTE_PATHS.MEMBER_ZONE}>
                      <Button variant="outline" size="sm" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                        <UserCircle className="h-4 w-4" />
                        會員專區
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <LogOut className="h-4 w-4" />
                    登出
                  </Button>
                </div>
              ) : (
                <Link to={ROUTE_PATHS.MEMBER_LOGIN}>
                  <Button variant="outline" size="sm" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    <LogIn className="h-4 w-4" />
                    會員專區
                  </Button>
                </Link>
              )}
            </nav>

            {/* Mobile hamburger */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <div className="flex flex-col gap-6 mt-8">
                  <div className="flex items-center gap-2 pb-4 border-b border-border">
                    <img
                      src="/images/hkcmprgu-logo-01.png"
                      alt="Logo"
                      className="h-10 w-auto"
                    />
                  </div>
                  {navItems.map((item) => (
                    <div key={item.href}>
                      <a
                        href={item.href}
                        onClick={(e) => { handleAnchorClick(e, item.href); }}
                        className="text-lg font-medium text-foreground hover:text-primary transition-colors block"
                      >
                        {item.label}
                      </a>
                      {item.label === '關於我們' && (
                        <Link
                          to={ROUTE_PATHS.ORG_CHART}
                          onClick={() => setIsOpen(false)}
                          className="block mt-1 ml-4 text-base text-muted-foreground hover:text-primary transition-colors"
                        >
                          └ 組織架構
                        </Link>
                      )}
                    </div>
                  ))}
                  {isLoggedIn ? (
                    <div className="flex flex-col gap-2">
                      {userRole === 'admin' ? (
                        <Link to={ROUTE_PATHS.MEMBER_DASHBOARD} onClick={() => setIsOpen(false)}>
                          <Button className="w-full gap-2" variant="outline">
                            <LayoutDashboard className="h-4 w-4" />
                            管理員後台
                          </Button>
                        </Link>
                      ) : (
                        <Link to={ROUTE_PATHS.MEMBER_ZONE} onClick={() => setIsOpen(false)}>
                          <Button className="w-full gap-2" variant="outline">
                            <UserCircle className="h-4 w-4" />
                            會員專區
                          </Button>
                        </Link>
                      )}
                      <Button
                        className="w-full gap-2"
                        variant="outline"
                        onClick={() => { setIsOpen(false); handleLogout(); }}
                      >
                        <LogOut className="h-4 w-4" />
                        登出
                      </Button>
                    </div>
                  ) : (
                    <Link to={ROUTE_PATHS.MEMBER_LOGIN} onClick={() => setIsOpen(false)}>
                      <Button className="w-full gap-2" variant="outline">
                        <LogIn className="h-4 w-4" />
                        會員專區
                      </Button>
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/images/hkcmprgu-logo-01.png"
                  alt="Logo"
                  className="h-12 w-auto"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              <h3 className="text-lg font-bold mb-2">香港中醫師權益總工會</h3>
              <p className="text-sm text-primary-foreground/80">
                守護中醫師權益，推動中醫藥發展
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">快速連結</h4>
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      onClick={(e) => handleAnchorClick(e, item.href)}
                      className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
                <li>
                  <Link
                    to={ROUTE_PATHS.MEMBER_LOGIN}
                    className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                  >
                    會員專區
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">聯絡我們</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>地址：油麻地窩打老道40號4至5號舖</li>
                <li>電話 / WhatsApp：+852 5228 4182</li>
                <li>電郵：inquiry@hkcmprgu.org.hk</li>
              </ul>
              <div className="mt-4">
                <a
                  href="https://www.facebook.com/p/%E9%A6%99%E6%B8%AF%E4%B8%AD%E9%86%AB%E5%B8%AB%E6%AC%8A%E7%9B%8A%E7%B8%BD%E5%B7%A5%E6%9C%83-61571583053507/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary-foreground hover:text-primary-foreground/80 transition-colors"
                >
                  <SiFacebook className="h-5 w-5" />
                  <span className="text-sm">關注我們</span>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/60">
            <p>© 2026 香港中醫師權益總工會 版權所有</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
